import React, {useState, useEffect} from "react";
import classNames from "classnames/bind";
import {useSpring, animated} from "react-spring";
import {useDrag} from "@use-gesture/react";

import placeholderImage from "./assets/placeholder.svg";

import {DietanamnezTileForm} from "../DietanamnezTileForm";

import {Tile} from "../../types/Tile";
import {DietanamnezItemForm} from "../../types/DietanamnezItem";

import styles from "./DietanamnezTile.module.css";

const cx = classNames.bind(styles);

type Props = {
  tile: Tile;
  nextTile: Tile | undefined;
  initialValues?: DietanamnezItemForm;
  onSubmit: React.ComponentProps<typeof DietanamnezTileForm>["onSubmit"];
  portions: number[];
  tutorialWasShown: boolean;
  errorMessage?: { name: string, message: string };
  setErrorMessage?: any;
  showCardChangeAnimation: boolean
  isFormSubmitting: boolean
};

export const DietanamnezTile: React.FC<Props> = ({
  tile,
  nextTile,
  initialValues,
  onSubmit,
  portions,
  tutorialWasShown,
  errorMessage,
  setErrorMessage,
  showCardChangeAnimation,
  isFormSubmitting
}) => {
  const [showFrequencyWindow, setShowFrequencyWindow] = useState<boolean>(false); // Состояние для окна частоты
  const [isSwipedRight, setIsSwipedRight] = useState<boolean>(false); // Состояние для того, была ли карточка сдвинута вправо
  const [isSwipedLeft, setIsSwipedLeft] = useState<boolean>(false); // Состояние для того, была ли карточка сдвинута влево
  const [isDragging, setIsDragging] = useState<boolean>(false); // Состояние для отслеживания начала свайпа
  const [hintText, setHintText] = useState<string | null>(null);
  const [hintVisible, setHintVisible] = useState(false);
  const [state, setState] = useState<"FILLED" | "DEFAULT" | "FIELD">(
    initialValues ? "FILLED" : "DEFAULT"
  );

  useEffect(() => {
    if (hintText) {
      setHintVisible(true);
      const timer = setTimeout(() => {
        setHintVisible(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [hintText]);

  /*Меняем state, если открыли окно с частотой употребления при вводе объёма порции*/
  useEffect(() => {
    if(state !== "FILLED" && showFrequencyWindow) {
      setState("DEFAULT")
    }
  }, [showFrequencyWindow])

  const hintSpring = useSpring({
    opacity: hintVisible ? 1 : 0,
    transform: hintVisible
      ? "translateY(0px)"
      : "translateY(-20px)",
    config: { duration: 300 },
  });

  const [{x, rotate}, api] = useSpring(() => ({x: 0, rotate: 0}));

  const maxX = 200;
  const speedMultiplier = 1;

  // Обработка жестов (свайп)
  const bind = useDrag(
    ({movement: [mx], memo = x.get(), active}) => {
      const newX = memo + mx * speedMultiplier;
      const boundedX = Math.min(Math.max(newX, -maxX), maxX); // Ограничиваем по maxX

      api.start({x: boundedX, rotate: boundedX * 0.1});

      if(newX > 0) {
        setHintText('Употребляю')
      }
      if(newX < 0) {
        setHintText('Не употребляю')
      }

      // Если карточка сдвинута вправо на 80% от maxX, показываем окно частоты
      if (boundedX > maxX * 0.8) {
        setIsSwipedRight(true);
        setIsSwipedLeft(false);
      } else if (boundedX < -maxX * 0.8) {
        setIsSwipedLeft(true);
        setIsSwipedRight(false);
      } else {
        setIsSwipedRight(false);
        setIsSwipedLeft(false);
      }

      // Отслеживаем начало свайпа
      if (active && !isDragging) {
        setIsDragging(true);
      }
      if (!active && isDragging) {
        setIsDragging(false);
      }

      return memo;
    },
    {
      axis: "x",
      limit: [-maxX, maxX],
    }
  );

  const handleDragEnd = () => {
    if (isSwipedRight && hintVisible) {
      setShowFrequencyWindow(true);
    }

    if(isSwipedLeft && hintVisible) {
      onSubmit()
    }
    api.start({x: 0, rotate: 0});
    setHintVisible(false);
    setHintText(null)
  };

  const handleDragStart = (e: React.DragEvent<HTMLImageElement>) => {
    e.preventDefault();
  };

  const getCardContent = (tile: Tile, animate?:boolean | undefined) => {
    return (
      <>
        <div className={cx("info")}>
          <p
            className={cx("category")}
            style={{
              transform: !animate || showCardChangeAnimation || isFormSubmitting ? "scale(1)" : "scale(0.8)"
            }}
          >
            {tile.___parent.label}
          </p>
          <h2
            className={cx("title")}
            style={{
              transform: !animate || showCardChangeAnimation || isFormSubmitting ? "scale(1)" : "scale(0.8)"
            }}
          >
            {tile.custom_наименование}
          </h2>
        </div>
        {tile.custom_картинка_с_порциями_url ? (
          <img
            alt=""
            className={cx("image")}
            src={tile.custom_картинка_с_порциями_url}
            onDragStart={handleDragStart}
          />
        ) : (
          <div className={cx("placeholder")}>
            <img
              src={placeholderImage}
              className={cx("placeholderImage")}
              alt=""
              onDragStart={handleDragStart}
              style={{
                transform: !animate || showCardChangeAnimation || isFormSubmitting ? "scale(1)" : "scale(0.8)"
              }}
            />
            <p
              className={cx("placeholderText")}
              style={{
                transform: !animate || showCardChangeAnimation || isFormSubmitting ? "scale(1)" : "scale(0.8)"
              }}
            >
              У нас нет изображения для этого блюда.
              <br/>
              <br/>
              Все в порядке, просто продолжайте.
            </p>
          </div>
        )}
      </>
    )
  }

  return (
    <article className={cx("container")}>
      {errorMessage?.name && (
        <div className={cx("notification")} onClick={() => setErrorMessage({name: "", message: ""})}>
          <span>{errorMessage?.message}</span>
        </div>
      )}
      {hintText &&
        <animated.div
          className={cx("hint",
            {["hint--eat"]: hintText === 'Употребляю'},
            {["hint--noEat"]: hintText === 'Не употребляю'},
          )}
          style={hintSpring}
        >
          {hintText}
        </animated.div>
      }
      <div className={cx("card", "card--static")}></div>
      <animated.div
        {...bind()}
        className={cx("card", {["card--blocked"]: state === "FILLED"})}
        onMouseUp={handleDragEnd}
        onTouchEnd={handleDragEnd}
        style={{
          transform: x.to((x:number) => `translateX(${x}px) rotate(${rotate.get()}deg)`),
          display: showCardChangeAnimation || isFormSubmitting || isFormSubmitting ? "none" : "flex",
        }}
      >
        {getCardContent(tile)}
      </animated.div>
      {nextTile && (
        <>
          <animated.div
            className={cx("card",  "card--next", {["card--blocked"]: state === "FILLED"})}
            style={{
              backgroundColor: isDragging || showCardChangeAnimation || isFormSubmitting ? "#FFFFFF" : "#E0E8F0",
              width: showCardChangeAnimation || isFormSubmitting ? "90%" : "80%",
              height: showCardChangeAnimation || isFormSubmitting ? "67%" : "59%",
              top: showCardChangeAnimation || isFormSubmitting ? '88px' : '80px',
            }}
          >
            {getCardContent(nextTile, true)}
          </animated.div>
          <div
            className={cx("card",  "card--last", {["card--blocked"]: state === "FILLED"})}
            style={{
              backgroundColor: showCardChangeAnimation || isFormSubmitting ? "#E0E8F0" : "#D3DFE8",
              width: showCardChangeAnimation || isFormSubmitting ? "80%" : "70%",
              height: showCardChangeAnimation || isFormSubmitting ? "59%" : "52%",
              top: showCardChangeAnimation || isFormSubmitting ? '80px' : '72px',
            }}
          />
          <div
            className={cx("card",  "card--animation", {["card--blocked"]: state === "FILLED"})}
            style={{
              backgroundColor: showCardChangeAnimation || isFormSubmitting ? "#D3DFE8" : "#ebf0f5",
              opacity: showCardChangeAnimation || isFormSubmitting ? "1" : "0",
            }}
          />
        </>
      )}
      <div className={cx("form", [{"form--fixed": !showFrequencyWindow}])}>
        <DietanamnezTileForm
          showFrequencyWindow={showFrequencyWindow}
          setShowFrequencyWindow={setShowFrequencyWindow}
          state={state}
          setState={setState}
          onSubmit={onSubmit}
          initialValues={initialValues}
          inputPlaceholder={tile.custom_плейсхолдер_для_выбора_порции}
          unitLabel={tile.custom_ед_изм.label}
          portions={portions}
          tutorialWasShown={tutorialWasShown}
          isFormSubmitting={isFormSubmitting}
        />
      </div>
    </article>
  );
};
